/**
 * learn-history-dialog.tsx — Nguồn tri thức "Lịch sử tin nhắn" trên trang Train AI.
 *
 * Port từ thiết kế train-ai.html (modal "Thêm nguồn tri thức" → tab 💬): cho AI
 * học GIỌNG ĐIỆU & MẪU CÂU của nhân viên thật từ hội thoại cũ. Kết quả là một
 * ĐỀ XUẤT cập nhật persona nằm chờ duyệt — người thật bấm Áp dụng mới có hiệu lực.
 * Backend: learn-history-routes.ts + master proposals; hook dùng chung với trang
 * Cải thiện AI (use-ai-improve.ts).
 */
import { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, FileUp, Loader2, Sparkles, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import {
  useAnalyzeHistory, useAnalyzeHistoryFile, useApplyProposal, useLearnChannels,
  useLogicProposals, useRejectProposal,
} from '@/hooks/use-ai-improve'

const ALL = '__all__'

export function LearnHistoryDialog({
  open, onOpenChange, botId, onApplied,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  /** Mở từ màn một nhân viên AI cụ thể → đề xuất áp đúng persona của bot đó (không phải tài liệu dùng chung). */
  botId?: string
  /** Gọi với nội dung persona vừa áp dụng — màn đang sửa cập nhật ngay vào ô nháp, không cần tải lại trang. */
  onApplied?: (personaPrompt: string) => void
}) {
  const { t } = useTranslation('aiKnow')
  const channelsQ = useLearnChannels()
  const pendingQ = useLogicProposals('pending')
  const analyze = useAnalyzeHistory()
  const analyzeFile = useAnalyzeHistoryFile()
  const apply = useApplyProposal()
  const reject = useRejectProposal()
  const fileRef = useRef<HTMLInputElement>(null)

  const [channelId, setChannelId] = useState<string>(ALL)
  const [days, setDays] = useState<string>('90')
  const [preferOrders, setPreferOrders] = useState(true)

  // Lọc đúng phạm vi: mở từ một bot → chỉ đề xuất của bot đó; mở không gắn bot (chưa có màn dùng) → chỉ đề xuất dùng chung.
  const pending = (pendingQ.data?.items ?? []).filter(
    (p) => (p.source === 'learn_history' || p.source === 'learn_history_file') && (botId ? p.targetId === botId : !p.targetId),
  )
  const busy = analyze.isPending || analyzeFile.isPending

  const run = () => {
    analyze.mutate(
      // days=0 = toàn bộ lịch sử (backend hiểu 0 là không giới hạn ngày)
      { channelId: channelId === ALL ? undefined : channelId, days: Number(days), preferOrders, botId },
      {
        onSuccess: (r) => toast.success(t('learn.createdFromHistory'), { description: r.rationale }),
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const onFile = (f: File | undefined) => {
    if (!f) return
    analyzeFile.mutate({ file: f, botId }, {
      onSuccess: (r) => toast.success(t('learn.createdFromFile'), { description: r.rationale }),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('learn.title')}</DialogTitle>
          <DialogDescription>
            <Trans t={t} i18nKey="learn.description" components={{ b: <b className="text-foreground" /> }} />
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[62vh] space-y-4 overflow-y-auto pr-1">
          {/* ── Cách 1: từ hội thoại trong hệ thống ── */}
          <div className="space-y-2.5 rounded-lg border p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('learn.way1')}
            </p>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1 text-sm">
                <b>{t('learn.accountTitle')}</b>
                <p className="text-xs text-muted-foreground">{t('learn.accountHint')}</p>
              </div>
              <Select value={channelId} onValueChange={setChannelId}>
                <SelectTrigger className="w-48 shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('learn.allAccounts')}</SelectItem>
                  {(channelsQ.data?.channels ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {t('learn.channelOption', { name: c.name || t('learn.unnamed'), n: c.conversations })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1 text-sm">
                <b>{t('learn.rangeTitle')}</b>
                <p className="text-xs text-muted-foreground">{t('learn.rangeHint')}</p>
              </div>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-48 shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">{t('learn.days30')}</SelectItem>
                  <SelectItem value="90">{t('learn.days90')}</SelectItem>
                  <SelectItem value="0">{t('learn.allTime')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1 text-sm">
                <b>{t('learn.ordersTitle')}</b>
                <p className="text-xs text-muted-foreground">{t('learn.ordersHint')}</p>
              </div>
              <Switch checked={preferOrders} onCheckedChange={setPreferOrders} />
            </div>
            <Button className="w-full" onClick={run} disabled={busy}>
              {analyze.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />}
              {analyze.isPending ? t('learn.analyzing') : t('learn.analyze')}
            </Button>
          </div>

          {/* ── Cách 2: tệp xuất chat ── */}
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('learn.way2')}
            </p>
            <input
              ref={fileRef} type="file" accept=".txt,.json,text/plain,application/json" className="hidden"
              onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
            />
            <Button variant="outline" className="w-full" disabled={busy} onClick={() => fileRef.current?.click()}>
              {analyzeFile.isPending ? <Loader2 className="animate-spin" /> : <FileUp />}
              {analyzeFile.isPending ? t('learn.analyzingFile') : t('learn.chooseFile')}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              {t('learn.fileNote')}
            </p>
          </div>

          {/* ── Đề xuất đang chờ duyệt ── */}
          {pending.length > 0 && (
            <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide">
                {t('learn.pending', { n: pending.length })}
              </p>
              {pending.map((p) => (
                <div key={p.id} className="rounded-md bg-background/70 p-2.5 text-sm">
                  <p className="leading-relaxed"><span className="text-muted-foreground">{t('learn.why')}</span> {p.rationale}</p>
                  <pre className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-muted/60 p-2 text-xs leading-relaxed">
                    {p.proposedValue}
                  </pre>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm" disabled={apply.isPending || reject.isPending}
                      onClick={() => apply.mutate(p.id, {
                        onSuccess: () => {
                          toast.success(t('learn.applied'))
                          onApplied?.(p.proposedValue)
                          onOpenChange(false)
                        },
                        onError: (e) => toast.error(apiError(e)),
                      })}
                    >
                      <Check /> {t('common:actions.apply')}
                    </Button>
                    <Button
                      size="sm" variant="outline" disabled={apply.isPending || reject.isPending}
                      onClick={() => reject.mutate(p.id, {
                        onSuccess: () => toast.success(t('learn.rejected')),
                        onError: (e) => toast.error(apiError(e)),
                      })}
                    >
                      <X /> {t('learn.reject')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
